import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Reveal from '../public/Reveal';
import { PageOpening, useFontsRefresh } from '../public/Journal';
import { gsap, EASE, prefersReducedMotion } from '../public/gsap';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const humanize = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const money = (n) => `$${Math.round(n).toLocaleString()}`;

// Public pricing, set as a fee schedule: one ruled ledger, mono figures.
// (The dashboard's Upgrade page keeps using PricingCards.)
const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [billingEnforced, setBillingEnforced] = useState(false);
  const [interval, setInterval] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const tableRef = useRef(null);
  useFontsRefresh();

  // Launch mode = billing not yet enforced. Everything is free; banner and badges show.
  const launchMode = !billingEnforced;

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/billing/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(Array.isArray(data) ? data : (data.plans || []));
        setBillingEnforced(Boolean(data.billing_enforced));
      }
    } catch (err) {
      console.error('Failed to fetch plans', err);
    } finally {
      setLoading(false);
    }
  };

  // Figures roll over when the billing interval changes.
  const firstRender = useRef(true);
  useLayoutEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (prefersReducedMotion() || !tableRef.current) return;
    gsap.fromTo(
      tableRef.current.querySelectorAll('.fee-amount'),
      { yPercent: 60, autoAlpha: 0 },
      { yPercent: 0, autoAlpha: 1, duration: 0.6, ease: EASE, stagger: 0.04 }
    );
  }, [interval]);

  const handleSelect = (plan) => {
    if (isAuthenticated) {
      navigate('/dashboard/upgrade', { state: { selectedPlan: plan.key, interval } });
    } else {
      navigate(`/signup?plan=${plan.key}`);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const compartments = plans.filter((p) => p.plan_type === 'compartment');
  const bundles = plans.filter((p) => p.plan_type === 'bundle');

  const getPrice = (plan) => {
    if (interval === 'yearly') {
      const savedPct = plan.price_monthly > 0
        ? Math.round(((plan.price_monthly * 12 - plan.price_yearly) / (plan.price_monthly * 12)) * 100)
        : 0;
      return { amount: plan.price_yearly / 12, billedLabel: `${money(plan.price_yearly)}/yr`, savedPct };
    }
    return { amount: plan.price_monthly, billedLabel: null, savedPct: 0 };
  };

  const individualTotal = interval === 'yearly'
    ? compartments.reduce((sum, p) => sum + p.price_yearly / 12, 0)
    : compartments.reduce((sum, p) => sum + p.price_monthly, 0);

  const ctaLabel = (plan) => (plan.trial_enabled ? 'Start free trial' : 'Subscribe');

  const actionClass = 'link-underline font-plex-mono text-[12px] font-medium uppercase tracking-[0.18em] text-stamp';
  const rowGrid = 'grid gap-4 py-8 md:grid-cols-12 md:gap-8';
  const mono = 'font-plex-mono text-[11.5px] uppercase tracking-[0.16em] text-ink/50 dark:text-paper/50';

  return (
    <div className="pb-24">
      <button onClick={handleBack} className={`${mono} link-underline mt-8 hover:text-ink dark:hover:text-paper`}>
        &larr; Back
      </button>

      <PageOpening
        section="§ Fees"
        label="Fee schedule"
        title={launchMode ? 'Start free. Upgrade when you’re ready.' : 'Choose your plan.'}
        intro={`JPRO does the compliance grind faster and cleaner: Average Income Test, intake, audits, and more. Core tools are free for good. The advanced compartments are ${launchMode ? 'free during launch, then move to a plan.' : 'available per compartment or together with All Access.'}`}
      />

      {/* Launch notice, typeset like a stamped memo */}
      {launchMode && (
        <Reveal className="relative mb-16 grid gap-4 border-y border-ink/70 py-6 sm:grid-cols-12 sm:gap-8 dark:border-paper/60">
          <p className="font-plex-mono text-[11.5px] uppercase tracking-[0.2em] text-stamp sm:col-span-3">Notice</p>
          <p className="font-plex text-[16.5px] leading-relaxed text-ink/80 sm:col-span-7 dark:text-paper/80">
            <span className="font-medium text-ink dark:text-paper">Free while we launch.</span> Every JPRO tool is
            unlocked, no credit card required. Create an account and start working today.
          </p>
          <span className="hidden -rotate-6 self-center justify-self-end border-[1.5px] border-stamp px-3 py-1.5 font-plex-mono text-[11px] font-medium uppercase tracking-[0.16em] text-stamp sm:col-span-2 sm:inline-block">
            Free during launch
          </span>
        </Reveal>
      )}

      {/* Interval */}
      <div className="mb-6 flex items-center justify-between gap-6">
        <p className={mono}>Schedule of fees · USD</p>
        <div role="group" aria-label="Billing interval" className="flex items-center gap-6">
          {[['monthly', 'Monthly'], ['yearly', 'Yearly · save']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setInterval(key)}
              aria-pressed={interval === key}
              className={`relative pb-1 font-plex-mono text-[12px] uppercase tracking-[0.16em] transition-colors ${interval === key ? 'text-ink dark:text-paper' : 'text-ink/40 hover:text-ink/70 dark:text-paper/40 dark:hover:text-paper/70'}`}
            >
              {label}
              <span
                aria-hidden="true"
                className={`absolute inset-x-0 bottom-0 h-px origin-left bg-stamp transition-transform duration-500 ease-out ${interval === key ? 'scale-x-100' : 'scale-x-0'}`}
              />
            </button>
          ))}
        </div>
      </div>

      <div ref={tableRef}>
        {/* Column heads */}
        <div className={`hidden border-t-2 border-ink/80 py-3 md:grid md:grid-cols-12 md:gap-8 dark:border-paper/70 ${mono}`}>
          <span className="md:col-span-1">No.</span>
          <span className="md:col-span-4">Item</span>
          <span className="md:col-span-4">Includes</span>
          <span className="md:col-span-3 text-right">Rate</span>
        </div>

        {/* Core: free forever */}
        <Reveal className={`${rowGrid} border-t border-ink/15 dark:border-paper/15`}>
          <p className="font-plex-mono text-[13px] text-ink/50 md:col-span-1 dark:text-paper/50">01</p>
          <div className="md:col-span-4">
            <h2 className="font-news text-[32px] leading-none tracking-[-0.01em]">Core</h2>
            <p className="mt-3 font-plex-mono text-[11.5px] uppercase tracking-[0.16em] text-stamp">Free forever</p>
          </div>
          <p className="font-plex text-[15.5px] leading-relaxed text-ink/75 md:col-span-4 dark:text-paper/75">
            Chat and Chat History, Projects, My Notes and My Tasks, Leaderboards, Documents, Request Services, and
            Industry Briefs. Yours the moment you sign up.
          </p>
          <div className="md:col-span-3 md:text-right">
            <p className="font-plex-mono text-[34px] leading-none tracking-[-0.02em]">$0</p>
            <p className="mt-2 font-plex-mono text-[11px] text-ink/50 dark:text-paper/50">No credit card required</p>
            <button onClick={() => navigate(isAuthenticated ? '/dashboard' : '/signup')} className={`${actionClass} mt-5`}>
              {isAuthenticated ? 'Go to dashboard' : 'Get started'}
            </button>
          </div>
        </Reveal>

        {loading && (
          <p className={`${mono} border-t border-ink/15 py-8 dark:border-paper/15`}>Loading fee schedule&hellip;</p>
        )}

        {!loading && plans.length === 0 && (
          <p className="border-t border-ink/15 py-8 font-plex text-[15.5px] text-ink/60 dark:border-paper/15 dark:text-paper/60">
            Compartment pricing is temporarily unavailable. Core tools are free to use now.
          </p>
        )}

        {/* Compartments */}
        {compartments.map((plan, i) => {
          const price = getPrice(plan);
          return (
            <Reveal key={plan.id} className={`${rowGrid} border-t border-ink/15 dark:border-paper/15`}>
              <p className="font-plex-mono text-[13px] text-ink/50 md:col-span-1 dark:text-paper/50">{String(i + 2).padStart(2, '0')}</p>
              <div className="md:col-span-4">
                <h2 className="font-news text-[32px] leading-none tracking-[-0.01em]">{plan.name}</h2>
                {plan.description && (
                  <p className="mt-3 font-plex text-[15px] leading-relaxed text-ink/65 dark:text-paper/65">{plan.description}</p>
                )}
                <p className="mt-3 font-plex-mono text-[11.5px] text-ink/50 dark:text-paper/50">{plan.included_seats} seats included</p>
              </div>
              <div className="md:col-span-4">
                {plan.features?.length > 0 && (
                  <ul className="font-plex text-[15px] leading-relaxed text-ink/75 dark:text-paper/75">
                    {plan.features.map((f) => (
                      <li key={f.key || f} className="border-b border-dotted border-ink/20 py-1.5 last:border-b-0 dark:border-paper/20">
                        {f.name || humanize(f)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="md:col-span-3 md:text-right">
                <p className="overflow-hidden font-plex-mono text-[34px] leading-none tracking-[-0.02em]">
                  <span className="fee-amount inline-block">{money(price.amount)}</span>
                  <span className="text-[14px] tracking-normal text-ink/45 dark:text-paper/45"> /mo</span>
                </p>
                {price.billedLabel && (
                  <p className="mt-2 font-plex-mono text-[11px] text-ink/50 dark:text-paper/50">
                    Billed at {price.billedLabel}
                    {price.savedPct > 0 && <span className="text-stamp"> · save {price.savedPct}%</span>}
                  </p>
                )}
                {launchMode && (
                  <p className="mt-2 font-plex-mono text-[11px] leading-relaxed text-stamp">
                    Free to use right now. This rate applies when launch pricing goes live.
                  </p>
                )}
                {plan.trial_enabled && (
                  <p className="mt-2 font-plex-mono text-[11px] text-ink/50 dark:text-paper/50">{plan.trial_days}-day free trial</p>
                )}
                <button onClick={() => handleSelect(plan)} className={`${actionClass} mt-5`}>{ctaLabel(plan)}</button>
              </div>
            </Reveal>
          );
        })}

        {/* Bundle: the total line of the schedule */}
        {bundles.map((plan) => {
          const price = getPrice(plan);
          const savings = individualTotal - price.amount;
          const savingsPct = individualTotal > 0 ? Math.round((savings / individualTotal) * 100) : 0;
          return (
            <Reveal key={plan.id} className={`${rowGrid} border-t-2 border-ink/80 dark:border-paper/70`}>
              <p className="font-plex-mono text-[13px] text-stamp md:col-span-1">Total</p>
              <div className="md:col-span-4">
                <h2 className="font-news text-[40px] leading-none tracking-[-0.015em]">{plan.name}</h2>
                <p className="mt-3 font-plex-mono text-[11.5px] uppercase tracking-[0.16em] text-stamp">
                  {savingsPct > 0 ? `Best value · save ${savingsPct}%` : 'Best value'}
                </p>
                {plan.description && (
                  <p className="mt-3 font-plex text-[15px] leading-relaxed text-ink/65 dark:text-paper/65">{plan.description}</p>
                )}
                <p className="mt-3 font-plex-mono text-[11.5px] text-ink/50 dark:text-paper/50">{plan.included_seats} seats included</p>
              </div>
              <div className="md:col-span-4">
                {plan.compartments && (
                  <ul className="font-plex text-[15px] leading-relaxed text-ink/75 dark:text-paper/75">
                    {plan.compartments.map((c) => (
                      <li key={c.key || c} className="border-b border-dotted border-ink/20 py-1.5 last:border-b-0 dark:border-paper/20">
                        {c.name || (typeof c === 'string' ? humanize(c) : c)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="md:col-span-3 md:text-right">
                {savingsPct > 0 && (
                  <p className="mb-2 font-plex-mono text-[12px] text-ink/40 line-through dark:text-paper/40">
                    {money(individualTotal)}/mo individually
                  </p>
                )}
                <p className="overflow-hidden font-plex-mono text-[44px] leading-none tracking-[-0.02em]">
                  <span className="fee-amount inline-block">{money(price.amount)}</span>
                  <span className="text-[14px] tracking-normal text-ink/45 dark:text-paper/45"> /mo</span>
                </p>
                {price.billedLabel && (
                  <p className="mt-2 font-plex-mono text-[11px] text-ink/50 dark:text-paper/50">Billed at {price.billedLabel}</p>
                )}
                {launchMode && <p className="mt-2 font-plex-mono text-[11px] text-stamp">Free during launch</p>}
                <button onClick={() => handleSelect(plan)} className={`${actionClass} mt-5`}>Get All Access</button>
              </div>
            </Reveal>
          );
        })}
        <div className="h-px bg-ink/15 dark:bg-paper/15" />
      </div>

      {/* Footnote */}
      <p className="mt-8 max-w-3xl font-plex-mono text-[12px] leading-relaxed text-ink/55 dark:text-paper/55">
        All prices are in USD. {launchMode ? 'Everything is free during our launch period. When pricing goes live, your free tools stay free and you’ll have a trial window before any paid tool locks. ' : ''}
        Existing Zeffert service clients always receive complimentary platform access.{' '}
        <Link to="/about" className="link-underline text-ink/80 dark:text-paper/80">About JPRO</Link>
      </p>
    </div>
  );
};

export default PricingPage;
